import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shirt, Goal, ChevronRight, ChevronLeft, Loader2, Copy, Check, Share2, Lock, Play, MessageCircle, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, shootoutApi, shareLink } from "../lib/api";

const IMG = "https://static.prod-images.emergentagent.com/jobs/b59e590a-c823-4f57-bec9-a8b31bc1b827/images/";
export const GAMES = [
  { id: "draft", Icon: Shirt, accent: "#FBBF24", bg: `${IMG}7131e3bd6ee1ee2cc789985b7ca41caf4478f38226a195288f2bf3c1d2138e8d.jpeg`, title: "Draft XI", sub: "Draft your XI, highest points wins", stakes: [10, 25, 50, 100] },
  { id: "penalties", Icon: Goal, accent: "#38BDF8", bg: `${IMG}7e07b7e7442eddce377c9519a23cda086e68a60cb26ca75ef4327a7f6936d8a4.jpeg`, title: "Penalty Shootout", sub: "Swipe to shoot and save, best of 5", stakes: [10, 20, 50] },
];

// Blurred game screenshot behind a big game button, plus a bold icon badge.
export const GameBg = ({ game }) => (
  <>
    <img src={game.bg} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-[3px] scale-110 pointer-events-none" />
    <span className="absolute inset-0 bg-gradient-to-r from-[#064E3B]/95 via-[#065F46]/80 to-[#047857]/55 pointer-events-none" />
  </>
);

export const GameBadge = ({ game, size = "w-14 h-14", icon = "w-8 h-8" }) => (
  <span className={`${size} rounded-2xl flex items-center justify-center shrink-0 bg-[#0F172A] ring-2 shadow-lg`} style={{ "--tw-ring-color": game.accent, boxShadow: `0 0 18px ${game.accent}66` }}>
    <game.Icon className={icon} style={{ color: game.accent }} strokeWidth={2.5} />
  </span>
);

export const createChallenge = async (game, stake, friend) => {
  if (game.id === "draft") {
    const id = (await api.createMatch(stake, "", friend)).match.id;
    return { link: shareLink("w", id), to: `/multiplayer/match/${id}` };
  }
  const id = (await shootoutApi.create(stake, friend)).shootout.id;
  return { link: shareLink("p", id), to: `/p/${id}` };
};

export const GameRow = ({ game, onClick, right, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-left transition-colors hover:border-[#10B981]/60 hover:bg-[#10B981]/10"
  >
    <span className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 bg-[#10B981]">
      <game.Icon className="w-5 h-5 text-[#0F172A]" />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block font-display font-bold text-base">{game.title}</span>
      <span className="block text-xs text-slate-400 mt-0.5">{game.sub}</span>
    </span>
    {right || <ChevronRight className="w-5 h-5 text-[#10B981] shrink-0" />}
  </button>
);

const StakeStep = ({ game, user, onBack, onCreated, onSignup, friend }) => {
  const [stake, setStake] = useState(game.stakes[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [soon, setSoon] = useState(false);
  const [picking, setPicking] = useState(false);
  const go = async () => {
    setBusy(true);
    setError(null);
    try { onCreated(await createChallenge(game, stake, friend)); } catch (e) { setError(e?.response?.data?.detail || "Couldn't create the link, try again"); }
    setBusy(false);
  };
  return (
    <div data-testid="wager-stake-step">
      <button data-testid="wager-back-btn" onClick={onBack} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" /> Back</button>
      <div className="font-display font-bold text-lg">{game.title}</div>
      {(
        <>
          <div className="mt-3 flex items-center justify-between rounded-xl border border-[#334155] bg-[#0F172A] px-3 py-2.5">
            <span data-testid="wager-stake-summary" className="text-sm"><span className="font-display font-bold text-lg">£{stake}</span> <span className="text-slate-400">stake · winner takes £{stake * 2}</span></span>
            <button data-testid="wager-change-stake-btn" onClick={() => setPicking((v) => !v)} className="text-xs font-semibold text-[#38BDF8] hover:text-white transition-colors">
              {picking ? "Done" : "Change stake"}
            </button>
          </div>
          {picking && (
          <>
          <div className={`grid gap-2 mt-2 ${game.stakes.length === 4 ? "grid-cols-4" : "grid-cols-3"}`}>
            {game.stakes.map((s) => (
              <button key={s} data-testid={`wager-stake-${s}`} onClick={() => { setStake(s); setPicking(false); }}
                className={`rounded-xl py-3 font-display font-bold text-sm border transition-colors ${stake === s ? "bg-[#10B981] text-[#0F172A] border-[#10B981]" : "bg-white/5 text-slate-200 border-white/10 hover:border-white/25"}`}>
                £{s}
              </button>
            ))}
          </div>
          <button data-testid="wager-stake-real-locked" onClick={() => setSoon(true)}
            className="mt-2 w-full rounded-xl border border-dashed border-[#F59E0B]/50 bg-[#F59E0B]/[0.07] px-3 py-2.5 flex items-center justify-between gap-2 text-left hover:bg-[#F59E0B]/[0.12] transition-colors">
            <span className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-[#F59E0B]" />
              <span>
                <span className="block font-display font-bold text-sm text-white">£20 stake · Win £40</span>
                <span className="block text-[11px] text-slate-400">Real money wagers</span>
              </span>
            </span>
            <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#F59E0B] bg-[#F59E0B]/15 rounded-full px-2 py-1">Coming soon</span>
          </button>
          {soon && <p data-testid="wager-real-locked-msg" className="text-[#F59E0B] text-xs mt-1.5 text-center">Locked, coming soon. Real money wagers aren't available yet.</p>}
          </>
          )}
        </>
      )}
      <button data-testid="wager-get-link-btn" onClick={go} disabled={busy} className="btn-green wager-glow w-full rounded-xl py-4 mt-4 font-extrabold text-lg flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />} {friend ? `Challenge ${friend}` : "Get challenge link"}
      </button>
      {error && <p data-testid="wager-error" className="text-[#EF4444] text-sm mt-2">{error}</p>}
    </div>
  );
};

export const LinkStep = ({ game, link, to, onOpen, friend }) => {
  const [copied, setCopied] = useState(false);
  const [more, setMore] = useState(false);
  const msg = `I challenge you to ${game.title} on Footie Clash. Tap to play:`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); } catch (e) { /* blocked */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Footie Clash", text: msg, url: link }); return; } catch (e) { if (e?.name === "AbortError") return; }
    }
    copy();
  };
  return (
    <div data-testid="wager-link-step">
      <div data-testid="wager-link-title" className="font-display font-bold text-lg">{friend ? `Challenge sent to ${friend}` : "Your challenge link is ready"}</div>
      <div className="text-xs text-slate-400 mt-1">{friend ? `They've got an alert and it's in their open clashes. You can also send them the link.` : "Send it to a mate. When they tap it, the game starts."}</div>
      <button data-testid="wager-share-btn" onClick={share} className="btn-green wager-glow w-full rounded-xl py-4 mt-4 font-extrabold text-lg flex items-center justify-center gap-2">
        <Share2 className="w-5 h-5" /> {copied ? "Link copied!" : "Share link"}
      </button>
      <button data-testid="wager-more-options-btn" onClick={() => setMore((v) => !v)} className="w-full mt-3 text-sm font-semibold text-slate-300 hover:text-white flex items-center justify-center gap-1 transition-colors">
        {more ? "Fewer options" : "More options"} <ChevronRight className={`w-4 h-4 transition-transform ${more ? "-rotate-90" : "rotate-90"}`} />
      </button>
      {more && (
      <div data-testid="wager-more-options">
      <div className="grid grid-cols-2 gap-2 mt-2">
        <a data-testid="wager-whatsapp-btn" href={`https://wa.me/?text=${encodeURIComponent(`${msg} ${link}`)}`} target="_blank" rel="noreferrer"
          className="rounded-xl py-3 font-bold text-sm text-white flex items-center justify-center gap-2 bg-[#128C7E] hover:bg-[#0f7a6d] transition-colors">
          <MessageCircle className="w-4 h-4" /> WhatsApp
        </a>
        <a data-testid="wager-imessage-btn" href={`sms:&body=${encodeURIComponent(`${msg} ${link}`)}`}
          className="rounded-xl py-3 font-bold text-sm text-white flex items-center justify-center gap-2 bg-[#0a84ff]/90 hover:bg-[#0a84ff] transition-colors">
          <MessageSquare className="w-4 h-4" /> iMessage
        </a>
      </div>
      <button data-testid="wager-copy-btn" onClick={copy} className="mt-2 w-full rounded-xl border border-white/10 bg-black/25 px-3 py-2.5 flex items-center gap-2 text-left hover:border-[#38BDF8]/40 transition-colors">
        <span data-testid="wager-link-text" className="flex-1 min-w-0 truncate text-xs text-slate-300">{link.replace(/^https?:\/\//, "")}</span>
        <span className="text-xs font-semibold text-[#38BDF8] flex items-center gap-1 shrink-0">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}{copied ? "Copied" : "Copy"}</span>
      </button>
      </div>
      )}
      <button data-testid="wager-open-game-btn" onClick={() => onOpen(to)} className="w-full mt-3 text-sm rounded-xl py-3 font-semibold text-white border border-white/12 bg-white/[0.04] hover:bg-white/[0.08] flex items-center justify-center gap-2">
        <Play className="w-4 h-4" /> Go to my game
      </button>
    </div>
  );
};

// Wager a Mate pop-up: pick a game, pick a stake, get a challenge link to share.
const WagerPicker = ({ open, onClose, user, onSignup, friend }) => {
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [created, setCreated] = useState(null);
  useEffect(() => { if (!open) { setGame(null); setCreated(null); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="wager-picker-modal" className="theme-light bg-[#1E293B] border border-[#10B981]/30 text-white w-[calc(100%-2rem)] max-w-sm p-6 rounded-2xl">
        <DialogTitle className="font-display font-extrabold text-2xl">{friend ? `Challenge ${friend}` : "Wager a mate"}</DialogTitle>
        <DialogDescription className="text-slate-400 text-sm">{created ? "Share it however you like." : friend ? "Pick a game and a stake." : "Pick a game and send your mate the link."}</DialogDescription>
        <div className="mt-3 min-w-0">
          {created ? (
            <LinkStep game={game} friend={friend} {...created} onOpen={(to) => { onClose(); navigate(to); }} />
          ) : game ? (
            <StakeStep game={game} user={user} friend={friend} onBack={() => setGame(null)} onCreated={setCreated} onSignup={() => { onClose(); onSignup(); }} />
          ) : (
            <div className="space-y-3">
              {GAMES.map((g) => <GameRow key={g.id} game={g} testid={`wager-picker-${g.id}`} onClick={() => setGame(g)} />)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WagerPicker;
